// Shared zoom-and-pan state + reset/step handlers for any screen that renders a
// MapView with the MapControlsBar beneath it. Zoom is always on; the default
// framing is fully zoomed out (MIN scale) and `reset` returns to it. Mirrors
// the math the in-game screen uses (rescale about the current centre).
import { useState, useCallback } from 'react'
import {
  clampZoomScale,
  rescaleAboutCenter,
  initialPanForFocus,
  MIN_ZOOM_SCALE,
  MAX_ZOOM_SCALE,
  ZOOM_STEP,
  type PanOffset,
} from './mapZoom'

interface ViewBox {
  x?: number
  y?: number
  width: number
  height: number
}

export interface MapZoomControls {
  scale: number
  pan: PanOffset
  setScale: (s: number) => void
  setPan: (p: PanOffset) => void
  zoomIn: () => void
  zoomOut: () => void
  reset: () => void
  canZoomIn: boolean
  canZoomOut: boolean
}

export function useMapZoomControls(viewBox: ViewBox | undefined): MapZoomControls {
  const [scale, setScale] = useState(MIN_ZOOM_SCALE)
  const [pan, setPan] = useState<PanOffset>({ tx: 0, ty: 0 })

  const zoomBy = useCallback(
    (dir: 1 | -1) => {
      if (!viewBox) return
      const next = clampZoomScale(scale + dir * ZOOM_STEP)
      if (next === scale) return
      setPan(rescaleAboutCenter(viewBox.x ?? 0, viewBox.y ?? 0, viewBox.width, viewBox.height, scale, next, pan))
      setScale(next)
    },
    [viewBox, scale, pan],
  )

  const reset = useCallback(() => {
    if (!viewBox) return
    setScale(MIN_ZOOM_SCALE)
    setPan(initialPanForFocus(viewBox.x ?? 0, viewBox.y ?? 0, viewBox.width, viewBox.height, MIN_ZOOM_SCALE))
  }, [viewBox])

  return {
    scale,
    pan,
    setScale,
    setPan,
    zoomIn: () => zoomBy(1),
    zoomOut: () => zoomBy(-1),
    reset,
    canZoomIn: scale < MAX_ZOOM_SCALE - 1e-6,
    canZoomOut: scale > MIN_ZOOM_SCALE + 1e-6,
  }
}
